"use client";

import { usePathname } from "next/navigation";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { ScrollSmoother } from "gsap/dist/ScrollSmoother";
import { useGSAP } from "@gsap/react";
import Header from "../Layout/Header";
import Footer from "../Layout/Footer";
import { useEffect, useRef } from "react";
import { ReactLenis } from "lenis/react";
import RouteScrollReset from "./RouteScrollReset";
import HashAnchorHandler from "./HashAnchorHandler";

// Ensure ScrollTrigger is always registered
gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

export default function ClientProviders({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const smootherRef = useRef<ScrollSmoother | null>(null);

  useGSAP(
    () => {
      // Nếu đã tồn tại thì bỏ qua
      if (!smootherRef.current) {
        smootherRef.current = ScrollSmoother.create({
          wrapper: "#smooth-wrapper",
          content: "#smooth-content",
          smooth: 1.8,
          effects: true,
          normalizeScroll: false,
        });
      }
      // Cleanup khi toàn bộ layout unmount (ít khi xảy ra)
      return () => {
        smootherRef.current?.kill();
        smootherRef.current = null;
        gsap.set(["#smooth-wrapper", "#smooth-content"], { clearProps: "all" });
      };
    },
    { scope: rootRef }, // rất quan trọng để GSAP gỡ đúng chỗ
  );

  // Mỗi lần đổi route: chỉ refresh (không tạo lại)
  useEffect(() => {
    // Delay 1 frame để chắc chắn children đã render xong
    const id = requestAnimationFrame(() => {
      ScrollTrigger.refresh(true);
      smootherRef.current?.refresh();
      // tuỳ chọn: scrollTo(0) khi vào trang mới
      // smootherRef.current?.scrollTo(0, false);
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  return (
    <>
      <Header />
      <div ref={rootRef}>
        <div id="smooth-wrapper">
          <div id="smooth-content">
            <MainContent>{children}</MainContent>
          </div>
        </div>
      </div>
    </>
  );
}

const MainContent = ({ children }: { children: React.ReactNode }) => {
  const scope = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      // Pin section khi scroll
      const root = scope.current!;
      const panels = gsap.utils.toArray<HTMLElement>(
        root.querySelectorAll("[data-panel]"),
      );

      panels.forEach((panel, i) => {
        const isMorePanel = i === 0;
        const isLast = i === panels.length - 1;

        ScrollTrigger.create({
          trigger: panel,
          start: "top-=80 top",
          end: isMorePanel ? "+=50%" : undefined,
          pin: !isLast, // panel cuối không pin
          pinSpacing: isMorePanel,
          anticipatePin: 1,
          refreshPriority: isMorePanel ? 1 : 0,
        });
      });

      const onLoad = () => ScrollTrigger.refresh();
      window.addEventListener("load", onLoad);
      return () => window.removeEventListener("load", onLoad);
    },
    { scope, revertOnUpdate: true },
  );

  return (
    <div ref={scope} className="relative">
      <main className="pt-[var(--header-h)]">{children}</main>
      <Footer />
    </div>
  );
};
